import React, { useState, memo, useCallback, forwardRef, useEffect, useRef } from 'react';
import { Tag, Tile, Tooltip } from '@carbon/react';
import PropTypes from 'prop-types';
import { useInView } from 'react-cool-inview';

import './_override.scss';

import cx from 'classnames';
import './ListItem.scss';

import {
  CheckmarkFilled,
  IncompleteWarning,
  ViewOff,
  ChartSpiral,
  ServerTime,
  CameraAction,
  Layers,
  StarFilled,
} from '@carbon/react/icons';
import { PreviewSlideshow } from '../Elements';

import moment from 'moment';
import 'moment/locale/de';

// How often to remount a failed <img>/<video> before surfacing the broken
// state. Safari aborts range requests when many media negotiate at once and
// never retries on its own, leaving tiles blank until a manual reload.
const MAX_LOAD_RETRIES = 3;


const ListItem = memo(forwardRef(
  ({
    item,
    onClick,
    noFooter=false,
    bottomFooter=false,
    size = 'normal',
    hooverFooter = false,
    isSelected = false,
    groups = [],
    noHighlight = false,
  }, ref) => {
    const isSafari = false; //IsSafari();

    // determine ratio
    let ratio = "listItemSize_ratio_equal";
    //if (item.real_width === item.real_height) { // 1920x1920
    //  ratio = styles.listItemSize_ratio_equal;
    //}

    if (item.real_width > item.real_height ) { // 1920x1080
      ratio = "listItemSize_ratio_with_gt_height";
    }

    if (item.real_width < item.real_height) { // 1080x1920
      ratio = "listItemSize_ratio_height_gt_width";
    }

    let size_class_name = "listItemSize_normal";
    let render_content = item.s3_med_url;
    switch (size) {
      case 'normal':
        render_content = item.s3_thumb_url;
        break;
      case 'small':
        render_content = item.s3_thumb_url;
        size_class_name = "listItemSize_small";
        break;
      case 'large':
        size_class_name = "listItemSize_large";
        break;
      default:
        break;
    }

    const [isLoaded, setIsLoaded] = useState(false);
    // Bumping loadKey remounts the media element, forcing a fresh request
    // after a Safari-aborted load. loadAttemptRef caps the retries so a
    // genuinely broken source eventually surfaces its error state.
    const [loadKey, setLoadKey] = useState(0);
    const loadAttemptRef = useRef(0);
    const retryTimerRef = useRef(null);

    const handleIsLoaded = useCallback(() => {
      setIsLoaded(true);
    }, [setIsLoaded]);

    // When the underlying media load *fails*, first retry a few times by
    // remounting the element (Safari aborts concurrent range requests and
    // won't reissue them itself). Once retries are exhausted we hide the
    // pixel placeholder so the operator sees the real broken-image / "no
    // source" state. A load that simply takes long (slow network, big video)
    // keeps the pixelated preview visible — a recognisable placeholder is
    // better than a blank tile.
    const handleLoadError = useCallback((label) => (event) => {
      console.warn(`listItem ${label} error`, item?.model?.ID, event?.target?.error || event?.target?.currentSrc);
      if (loadAttemptRef.current < MAX_LOAD_RETRIES) {
        loadAttemptRef.current += 1;
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        // Small staggered backoff so retries don't all fire in the same
        // tick and recreate the same request pile-up that failed.
        retryTimerRef.current = setTimeout(() => {
          setLoadKey((k) => k + 1);
        }, 400 * loadAttemptRef.current);
        return;
      }
      setIsLoaded(true);
    }, [item?.model?.ID]);

    // Reset retry bookkeeping when the tile is reused for a different item
    // (list reorder keeps the component mounted but swaps props), and clear
    // any pending retry timer on unmount.
    useEffect(() => {
      loadAttemptRef.current = 0;
      setLoadKey(0);
      setIsLoaded(false);
      return () => {
        if (retryTimerRef.current) {
          clearTimeout(retryTimerRef.current);
          retryTimerRef.current = null;
        }
      };
    }, [item?.model?.ID]);

    // Defer the heavy <img>/<video> until the tile is close to the viewport.
    // Before that, only the tiny pixel-thumbnail shows. This stops the
    // browser from kicking off N concurrent HTTP-206 range requests on
    // page load — Safari in particular falls over when too many videos
    // negotiate ranges at the same time and aborts requests with an error.
    // `rootMargin` pre-loads ~one viewport ahead so scroll feels seamless.
    // `unobserveOnEnter` keeps the media mounted once seen, so scrolling
    // back doesn't re-request.
    const { observe, inView } = useInView({
      rootMargin: '600px 0px',
      unobserveOnEnter: true,
    });

    // Merge the lazy-load observer with the forwarded ref (the gallery
    // uses one of these tiles as its "load next page" sentinel).
    const setRefs = useCallback((node) => {
      observe(node);
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    }, [observe, ref]);

    let renderable_content = <></>;

    // When the worker hasn't finished converting yet, the row exists but
    // every S3 URL is still an empty string. Rendering a <video src="">
    // produces a DEMUXER error in the console and a broken-frame
    // appearance. Detect that state and render a clean "Wird
    // verarbeitet"-placeholder until the next refresh picks up the
    // worker's result.
    const isProcessing = !item.items
      && !item.s3_thumb_url
      && !item.s3_med_url
      && !item.s3_url
      && !item.s3_pixel_thumbnail_url;

    if (item.items) {
      renderable_content = (
        <PreviewSlideshow
          elements={item.items}
          orientation={item.orientation}
          nowebgl
        />
      );
    } else if (isProcessing) {
      renderable_content = (
        <div className="listItem-processing">
          <span className="listItem-processing__label">
            {item.type === 1 ? 'Video wird verarbeitet…' : 'Bild wird verarbeitet…'}
          </span>
          <span className="listItem-processing__hint">
            Sobald die Konvertierung abgeschlossen ist, erscheint das Element automatisch.
          </span>
        </div>
      );
    } else {
      if (item.type === 1) {
        renderable_content = (
          <>
            <img className={cx(isLoaded === true ? "listItem-loading_pixel_hide" : "listItem-loading_pixel")} src={item.s3_pixel_thumbnail_url} width="240" height="240"/>
            {inView && (
              <video
                key={`media-${loadKey}`}
                src={`${item.s3_thumb_url}#t=2`}
                // metadata is enough for the first frame; auto would pull
                // the whole file (and many at once would spam the network).
                preload="metadata"
                // iOS Safari refuses to autoplay inline without this and
                // will pop the video into the system fullscreen player.
                playsInline
                onMouseOver={(event) => {
                  event.target.controls = true;
                }}
                onMouseOut={(event) => {
                  event.target.controls = false;
                }}
                loop
                autoPlay
                onCanPlay={handleIsLoaded}
                onLoadedData={handleIsLoaded}
                onError={handleLoadError('video')}
                muted
                style={isLoaded === false ? { width: 0 } : {}}
                type="video/mp4"
              >
              </video>
            )}
          </>
        )
      } else {
        renderable_content = (
          <>
            <img className={cx(isLoaded === true ? "listItem-loading_pixel_hide" : "listItem-loading_pixel")} src={item.s3_pixel_thumbnail_url} width="240" height="240"/>
            {inView && (
              <img
                key={`media-${loadKey}`}
                src={render_content}
                alt={item.filename}
                loading="lazy"
                onLoad={handleIsLoaded}
                onError={handleLoadError('image')}
              />
            )}
          </>
        )

      }
    }

    return (
      <div className={"listItem"} onClick={() => onClick(item)} ref={setRefs}>
        <Tile
          className={cx(
            (item.isSelected || isSelected) ? "listItemTile_selected" : null,
            "listItemTile",
            size_class_name,
            ratio ,
            isSafari
              ? item.type === 1
                ? "listItemTile_video"
                : "listItemTile_image"
              : null,
            hooverFooter ? 'showItemOnHover' : null,
            bottomFooter ? 'bottom-footer' : null,
            noHighlight === false && item?.highlighted === 1 ? 'highlighted' : null
          )}
          selected={(item.isSelected || isSelected)}>
          {renderable_content}

          {(!noFooter || hooverFooter) && (
            <div
              className={cx(["listItem-footer", hooverFooter ? 'onHoverItem' : null, bottomFooter ? 'bottom-footer' : 'no-bottom-footer'])}>
              <div className={"listItem-footerHead"}>{item.name}</div>
              <div className={"listItem-footerBody"}>{item.description || item.notes}</div>
              <div className={"listItem-footerFooter"}><ServerTime /><span>{moment(item.model?.UpdatedAt).locale('de').fromNow()}</span></div>
              {(item._pair_partner || item._is_variant_group) && (
                <div className="listItem-footerIndicators">
                  {item._pair_partner && (
                    <span><CameraAction size={14} /> Hoch+Quer</span>
                  )}
                  {item._is_variant_group && (
                    <span><Layers size={14} /> Varianten</span>
                  )}
                </div>
              )}
              { (size === 'large' && groups && groups.length > 0) &&
                <div className={"listItem-footerBody"}>{(item.groups || []).map(group => <Tag type="teal">{(groups || []).find(g => g.model.ID === group.model.ID)?.name}</Tag>)}</div>
              }
            </div>
          )}
          <div className={"listItem-shadow_on_hover"}></div>
          {item.dirty === 1 && (
            <div className="top-left">
              <Tooltip label="Der Stream hat Änderungen die noch nicht bestätigt wurden.">
                <button className="sb-tooltip-trigger" type="button">
                  <IncompleteWarning size={32} />
                </button>
              </Tooltip>
            </div>
          )}
          {!(item.isSelected || isSelected) || (
            <div className="bottom-right">
              <CheckmarkFilled size={32} />
            </div>
          )}

          {item.public === false && item.organisation_id === 0 && (
            <div className="top-left">
              <Tooltip label="Dieses Element ist nicht Öffentlich sichtbar.">
                <button className="sb-tooltip-trigger" type="button">
                  <ViewOff size={32} />
                </button>
              </Tooltip>
            </div>
          )}
          {item.action_stream === true && (
            <div className="top-left second">
              <Tooltip label="Dieser Stream ist ein Aktions-Stream">
                <button className="sb-tooltip-trigger" type="button">
                  <ChartSpiral size={32} />
                </button>
              </Tooltip>
            </div>
          )}
          {item._pair_partner && (
            <div className="pair-indicator">
              <Tooltip
                align="top"
                label={`Hoch+Quer-Paar mit „${item._pair_partner.name}". Beide Formate zeigen denselben Inhalt — der Player wählt zur Laufzeit das Format passend zum Gerät aus.`}
              >
                <button className="sb-tooltip-trigger" type="button">
                  <CameraAction size={16} />
                </button>
              </Tooltip>
            </div>
          )}
          {item._is_variant_group && (
            <div
              className={cx(
                'variant-group-indicator',
                !item._pair_partner && 'variant-group-indicator--solo'
              )}>
              <Tooltip
                align="top"
                label={`Varianten-Gruppe „${item.name}" mit ${item._variant_count || 0} benannten Varianten. Im Stream wählt der Nutzer eine Variante aus dem Dropdown — z. B. unterschiedliche Datums- oder Preis-Versionen desselben Layouts.`}
              >
                <button className="sb-tooltip-trigger" type="button">
                  <Layers size={16} />
                </button>
              </Tooltip>
            </div>
          )}
          {(noHighlight === false && (item?.new === 1  && (!item.highlighted || item.highlighted === 0))) && (
            <span className="corner-new">
              <StarFilled />
              Neu
            </span>
          )}
        </Tile>
      </div>
    );
  }
));

ListItem.propTypes = {
  item: PropTypes.object,
  onClick: PropTypes.func,
  custom: PropTypes.element,
  size: PropTypes.oneOf(['small', 'normal', 'large']),
  noFooter: PropTypes.bool,
  bottomFooter: PropTypes.bool,
  isSelected: PropTypes.bool,
  hooverFooter: PropTypes.bool,
};

export default ListItem;
